/**
 * Analytics Module — Client-Side Event Queue (WS-6)
 *
 * IndexedDB-persisted offline retry queue for analytics events on the web surface.
 * Events are stored in IndexedDB and flushed to /v1/events/batch.
 *
 * Surface detection: resolves to 'desktop' when platform is 'ERA' (ERA Desktop embeds
 * FusionWeb in a frame); defaults to 'web' for all other platforms.
 *
 * Spec: docs/plans/2026-04-08-rey-analytics-phase1-workstreams.md § WS-6
 *
 * Usage:
 *   await window.Analytics.init({ analyticsBaseUri, sessionToken, userId,
 *                                  dealershipId, platform, appVersion });
 *   await window.Analytics.enqueue('event_name', { session_id: '...', ... });
 *   await window.Analytics.shutdown();  // on session end / beforeUnmount
 */
const Analytics = {

    // --- Configuration ---
    _analyticsBaseUri: '',
    _sessionToken: '',
    _userId: '',
    _dealershipId: '',
    _platform: '',
    _appVersion: '1.0.0',
    _surface: 'web',

    // --- Surface token ---
    _surfaceToken: null,
    _surfaceTokenExpiresAt: 0,        // epoch ms
    _tokenRefreshTimeoutId: null,

    // --- IndexedDB ---
    _db: null,
    _DB_NAME: 'rey_analytics_web',
    _STORE_NAME: 'events',
    _SESSION_STATE_STORE: 'session_state',

    // --- Spec constants ---
    _MAX_QUEUE_SIZE: 500,
    _TTL_MS: 72 * 60 * 60 * 1000,   // 72 hours
    _FLUSH_INTERVAL_MS: 30 * 1000,   // 30 seconds
    _BATCH_SIZE: 50,
    _TOKEN_TTL_MS: 300 * 1000,       // 300 seconds (server-issued TTL)
    _TOKEN_REFRESH_EARLY_MS: 30 * 1000,

    // --- State ---
    _flushIntervalId: null,
    _pendingHealthCheck: false,
    _isInitialized: false,
    _shutdownPending: null,  // synchronous slot for page-unload event (avoids IDB write/read race)
    _isShuttingDown: false,
    _backgroundFlushPromise: null, // tracks an in-progress flushOnBackground so shutdown() can wait for it

    // ---------------------------------------------------------------------------
    // Public API
    // ---------------------------------------------------------------------------

    /**
     * Initialise the analytics module. Must be called once after user is authenticated.
     *
     * @param {Object} config
     * @param {string} config.analyticsBaseUri  SparkApi base URL (no trailing slash)
     * @param {string} config.sessionToken      Fusion bearer token used to acquire surface token
     * @param {string} config.userId            Opaque user identifier
     * @param {string} [config.dealershipId]    Dealership identifier (required for analytics JWT)
     * @param {string} [config.platform]        'ERA' | 'FOCUS' | 'ReverseRisk' | 'AutoVision' | 'ReconVision'
     * @param {string} [config.appVersion]      Semver string, e.g. '1.2.3'
     */
    async init(config) {
        if (this._isInitialized) return;

        this._analyticsBaseUri = (config.analyticsBaseUri || '').replace(/\/$/, '');
        this._sessionToken     = config.sessionToken  || '';
        this._userId           = config.userId        || '';
        this._dealershipId     = config.dealershipId  || '';
        this._platform         = config.platform      || '';
        this._appVersion       = config.appVersion    || '1.0.0';
        this._surface          = this._detectSurface();

        try {
            this._db = await this._openDb();
        } catch (err) {
            console.error('[Analytics] IndexedDB unavailable — module disabled:', err);
            return;
        }

        window.addEventListener('online',  () => this._onReconnect());
        window.addEventListener('offline', () => console.log('[Analytics] Offline — events will queue'));

        // Non-blocking token acquisition; queue accepts events before token is ready
        this._acquireToken().catch(err => console.warn('[Analytics] Initial token acquisition failed:', err));

        // Periodic flush — gates on health check if a prior 5xx flagged one as needed
        this._flushIntervalId = setInterval(async () => {
            try {
                if (this._pendingHealthCheck) {
                    const healthy = await this._healthCheck();
                    if (!healthy) return;
                    this._pendingHealthCheck = false;
                }
                await this._flush();
            } catch (err) {
                console.warn('[Analytics] Flush error:', err);
            }
        }, this._FLUSH_INTERVAL_MS);

        this._isInitialized = true;
        console.log('[Analytics] Initialized. Surface:', this._surface);
    },

    /**
     * Enqueue an analytics event. The module merges global properties (user_id,
     * dealership_id, platform, surface, app_version) automatically.
     * The caller must supply session_id (and any event-specific properties).
     *
     * @param {string} eventName   Snake_case event name, e.g. 'rey_session_started'
     * @param {Object} properties  Event-specific properties. Must include session_id.
     */
    async enqueue(eventName, properties) {
        if (!this._db) {
            console.warn('[Analytics] Not initialised — dropping event:', eventName);
            return;
        }

        const record = {
            event:           eventName,
            event_schema_v:  1,
            timestamp:       new Date().toISOString(),
            enqueuedAt:      Date.now(),
            properties: {
                user_id:       this._userId,
                dealership_id: this._dealershipId,
                platform:      this._platform,
                surface:       this._surface,
                app_version:   this._appVersion,
                ...properties,          // caller can supply/override any field
            }
        };

        try {
            await this._purgeStale();
            await this._enforceCap();
            await this._dbAdd(record);
        } catch (err) {
            console.error('[Analytics] Failed to enqueue event:', eventName, err);
        }
    },

    /**
     * Flush queued events without stopping the module. Call when the app is backgrounded.
     * Uses keepalive:true so the request can outlive a visibility-hidden transition.
     * Does NOT stop the flush interval or token refresh timers.
     *
     * The promise is stored in _backgroundFlushPromise so shutdown() can wait for it and
     * avoid re-sending the same IDB events when visibilitychange and pagehide fire together.
     */
    flushOnBackground() {
        this._backgroundFlushPromise = this._flush(true)
            .catch(err => console.warn('[Analytics] Background flush failed:', err))
            .finally(() => { this._backgroundFlushPromise = null; });
        return this._backgroundFlushPromise;
    },

    /**
     * Synchronously stage an event to be included in the next keepalive flush via shutdown().
     * Use this instead of enqueue() on the page-unload path so the event is available
     * immediately — enqueue() is async (3 IDB awaits) and races the shutdown() flush.
     *
     * @param {string} eventName   Snake_case event name
     * @param {Object} properties  Event-specific properties including session_id
     */
    enqueueForShutdown(eventName, properties) {
        if (!this._isInitialized) return;
        this._shutdownPending = {
            event:          eventName,
            event_schema_v: 1,
            timestamp:      new Date().toISOString(),
            properties: {
                user_id:       this._userId,
                dealership_id: this._dealershipId,
                platform:      this._platform,
                surface:       this._surface,
                app_version:   this._appVersion,
                ...properties,
            }
        };
    },

    /**
     * Flush queued events and stop the module. Call on true page unload or beforeUnmount.
     * Uses keepalive:true so the request outlives the page.
     *
     * On the pagehide path the browser terminates the JS execution context after the
     * event handler returns, so every `await` below the first one may never resume.
     * To guarantee rey_session_ended is delivered, we fire a synchronous (no-await)
     * keepalive fetch for _shutdownPending BEFORE the first await, while the page
     * context is still alive. The async _flush() below handles any remaining IDB events
     * on a best-effort basis (e.g. from beforeUnmount during SPA navigation).
     */
    async shutdown() {
        this._isShuttingDown = true;
        if (this._flushIntervalId) {
            clearInterval(this._flushIntervalId);
            this._flushIntervalId = null;
        }
        if (this._tokenRefreshTimeoutId) {
            clearTimeout(this._tokenRefreshTimeoutId);
            this._tokenRefreshTimeoutId = null;
        }

        // Synchronous fast path: fire the shutdown event before any await so it
        // reaches the browser's keepalive queue while the JS context is still alive.
        // Only possible when the surface token is already cached (no network round-trip).
        if (this._shutdownPending && this._surfaceToken && Date.now() < this._surfaceTokenExpiresAt) {
            const pending = this._shutdownPending;
            this._shutdownPending = null;
            fetch(`${this._analyticsBaseUri}/v1/events/batch`, {
                method:    'POST',
                keepalive: true,
                headers: {
                    'Authorization':        `Bearer ${this._surfaceToken}`,
                    'Content-Type':         'application/json',
                    'X-Rey-Surface':        this._surface,
                    'X-Rey-Client-Version': this._appVersion
                },
                body: JSON.stringify([{
                    event:          pending.event,
                    event_schema_v: pending.event_schema_v,
                    timestamp:      pending.timestamp,
                    properties:     pending.properties,
                }])
            }); // intentionally fire-and-forget — keepalive ensures delivery after page termination
        }

        // If flushOnBackground fired just before this (visibilitychange → pagehide path), wait
        // for it to complete and delete its records before running our own flush. This prevents
        // both flushes from reading the same IDB events and sending them twice.
        if (this._backgroundFlushPromise) {
            try { await this._backgroundFlushPromise; } catch (_) { /* already logged */ }
        }

        try {
            await this._flush(true /* keepalive */);
        } catch (err) {
            console.warn('[Analytics] Shutdown flush failed:', err);
        }
        this._shutdownPending = null;
        this._isShuttingDown = false;
    },

    /**
     * Write (or overwrite) the active session state to IndexedDB, keyed by session_id.
     * Each tab maintains its own record so concurrent tabs do not clobber each other.
     *
     * @param {{ session_id: string, started_at: number, action_count: number, last_active_at: number }} state
     */
    async persistSessionState(state) {
        if (!this._db) return;
        try {
            await this._sessionStatePut({ id: state.session_id, ...state });
        } catch (err) {
            console.warn('[Analytics] Failed to persist session state:', err);
        }
    },

    /**
     * Remove the persisted state for a specific session. Call after a session closes cleanly
     * so the next page load does not treat it as abandoned.
     *
     * @param {string} sessionId
     */
    async clearSessionState(sessionId) {
        if (!this._db) return;
        try {
            await this._sessionStateDelete(sessionId);
        } catch (err) {
            console.warn('[Analytics] Failed to clear session state:', err);
        }
    },

    /**
     * Read-only snapshot of a session_state record, or null if missing. Used by endReySession
     * to grab the authoritative cross-frame action_count right before emitting rey_session_ended,
     * covering the case where the owner frame was idle while guest frames accumulated actions.
     *
     * @param {string} sessionId
     * @returns {Promise<{ id: string, session_id: string, started_at: number, action_count: number, last_active_at: number } | null>}
     */
    async getSessionState(sessionId) {
        if (!this._db) return null;
        try {
            return await this._sessionStateGet(sessionId);
        } catch (err) {
            console.warn('[Analytics] Failed to read session state:', err);
            return null;
        }
    },

    /**
     * Atomically update last_active_at on an existing session_state record. The record's
     * action_count is preserved (not overwritten) so cross-frame counts don't clobber each other.
     *
     * Resolves to the updated record if it existed; resolves to null if the record was not
     * found (zombie prevention — owner already cleared it, caller should self-clean).
     * Rejects on IDB errors — callers should catch and NOT self-clean on transient failures,
     * since the record may still be intact.
     *
     * @param {string} sessionId
     * @returns {Promise<{ id: string, session_id: string, started_at: number, action_count: number, last_active_at: number } | null>}
     */
    touchSessionStateIfExists(sessionId) {
        if (!this._db) return Promise.reject(new Error('Analytics IndexedDB unavailable'));
        return this._sessionStateTouchIfExists(sessionId);
    },

    /**
     * Atomically increment action_count on an existing session_state record and refresh
     * last_active_at. IDB serialises readwrite transactions on the store, so concurrent
     * increments from sibling iframes compose correctly (count == owner + guest contributions).
     *
     * Resolves to the updated record if it existed; resolves to null if the record was not
     * found (owner cleared it). Rejects on IDB errors — see touchSessionStateIfExists.
     *
     * @param {string} sessionId
     * @returns {Promise<{ id: string, session_id: string, started_at: number, action_count: number, last_active_at: number } | null>}
     */
    incrementSessionActionCount(sessionId) {
        if (!this._db) return Promise.reject(new Error('Analytics IndexedDB unavailable'));
        return this._sessionStateIncrementActionCount(sessionId);
    },

    /**
     * Atomically get-and-delete a session_state record in a single IDB transaction. Used by
     * recovery so sibling iframes can't both emit rey_session_ended for the same abandoned
     * record. Whichever iframe's transaction commits first gets the record back; the other
     * iframe's get returns null (record already gone) and it skips emitting.
     *
     * Resolves to the deleted record, or null if the record didn't exist. Rejects on IDB errors.
     *
     * @param {string} sessionId
     * @returns {Promise<{ id: string, session_id: string, started_at: number, action_count: number, last_active_at: number } | null>}
     */
    claimAbandonedSession(sessionId) {
        if (!this._db) return Promise.reject(new Error('Analytics IndexedDB unavailable'));
        return this._sessionStateClaimAndDelete(sessionId);
    },

    /**
     * Read all persisted session state records without modifying them.
     * Returns an array so the caller can recover any tab's abandoned sessions.
     *
     * @returns {Promise<Array<{ session_id: string, started_at: number, action_count: number, last_active_at: number }>>}
     */
    async recoverAbandonedSessions() {
        if (!this._db) return [];
        try {
            return await this._sessionStateGetAll();
        } catch (err) {
            console.warn('[Analytics] Failed to read session state:', err);
            return [];
        }
    },

    /**
     * Return the fresh persisted session with the EARLIEST started_at, or null if none exist.
     * Freshness is measured from last_active_at (fallback: started_at). The "earliest started"
     * rule establishes a single canonical session across iframes — both adoption (no exclude)
     * and race-mitigation post-create (excludes caller's own session_id) use the same ranking
     * so every frame agrees on which session is authoritative.
     *
     * @param {number}  thresholdMs        Only sessions touched within this many ms are considered fresh.
     * @param {string=} excludeSessionId   Optional — caller's own session_id to exclude (race-mitigation).
     * @returns {Promise<{ id: string, session_id: string, started_at: number, action_count: number, last_active_at: number } | null>}
     */
    async findEarliestFreshSession(thresholdMs, excludeSessionId = null) {
        if (!this._db) return null;
        try {
            const all = await this._sessionStateGetAll();
            const now = Date.now();
            const fresh = all.filter(r => r.session_id !== excludeSessionId && now - (r.last_active_at ?? r.started_at) < thresholdMs);
            if (!fresh.length) return null;
            return fresh.reduce((best, r) => r.started_at < best.started_at ? r : best);
        } catch (err) {
            console.warn('[Analytics] Failed to read session state:', err);
            return null;
        }
    },

    // ---------------------------------------------------------------------------
    // Internal
    // ---------------------------------------------------------------------------

    async _onReconnect() {
        console.log('[Analytics] Online — checking health before flush');
        const healthy = await this._healthCheck();
        if (healthy) {
            this._pendingHealthCheck = false;
            this._flush().catch(err => console.warn('[Analytics] Reconnect flush error:', err));
        } else {
            console.warn('[Analytics] Health check failed on reconnect — will retry on next interval');
        }
    },

    // Cross-iframe coordination: IndexedDB is origin-scoped, so every Fusion iframe on the
    // page has its own Analytics instance with its own setInterval flush timer but they all
    // read/write the same events store. Without a lock, each iframe's timer pulls the same
    // queued records and POSTs them — the server sees each record N times (where N is the
    // number of iframes) and flags N-1 as is_duplicate. Use the Web Locks API so only one
    // iframe flushes at a time across the whole origin. Skip if another iframe holds the lock.
    //
    // Teardown paths (keepalive === true, i.e. shutdown() and flushOnBackground()) bypass the
    // lock. Two reasons: (1) with `ifAvailable: true`, a sibling iframe holding the lock at the
    // moment of pagehide causes our flush to be silently skipped — rey_session_ended then stays
    // stuck in IDB and surfaces later as 'abandoned' instead of 'user_closed'. (2) Lock
    // acquisition adds extra async hops between pagehide and the fetch call, and the browser
    // may not keep the page alive long enough to complete them.
    // Within a single Analytics instance, same-page duplicate teardown flushes (visibilitychange
    // hidden then pagehide firing together) are prevented by _backgroundFlushPromise tracking in
    // shutdown(). Cross-iframe duplicates on teardown are absorbed by server dedup.
    async _flush(keepalive) {
        if (keepalive === true || !navigator.locks) return this._doFlush(keepalive);
        return navigator.locks.request('rey_analytics_flush', { ifAvailable: true }, async lock => {
            if (!lock) return;   // another iframe is flushing right now; our turn will come
            return this._doFlush(keepalive);
        });
    },

    async _doFlush(keepalive) {
        if (!this._db || !navigator.onLine) return;

        await this._purgeStale();

        const records = await this._dbGetOldest(this._BATCH_SIZE);

        // During shutdown, prepend the synchronous event (enqueueForShutdown) so it is always
        // included in the keepalive request even if the IDB write hasn't completed yet.
        const pending = (this._isShuttingDown && this._shutdownPending) ? [this._shutdownPending] : [];
        const allRecords = [...pending, ...records];
        if (allRecords.length === 0) return;

        const token = await this._acquireToken();
        if (!token) {
            console.warn('[Analytics] No surface token — skipping flush');
            return;
        }

        const payload = allRecords.map(r => ({
            event:          r.event,
            event_schema_v: r.event_schema_v,
            timestamp:      r.timestamp,
            properties:     r.properties
        }));

        let response;
        try {
            response = await fetch(`${this._analyticsBaseUri}/v1/events/batch`, {
                method:    'POST',
                keepalive: keepalive === true,
                headers: {
                    'Authorization':        `Bearer ${token}`,
                    'Content-Type':         'application/json',
                    'X-Rey-Surface':        this._surface,
                    'X-Rey-Client-Version': this._appVersion
                },
                body: JSON.stringify(payload)
            });
        } catch (err) {
            // Network failure — leave events in queue for next attempt
            console.warn('[Analytics] Batch request failed (network):', err);
            return;
        }

        const ids = records.map(r => r.id);

        if (response.status === 202) {
            await this._dbDeleteBatch(ids);
            console.log('[Analytics] Flushed', records.length, 'event(s)');
        } else if (response.status >= 400 && response.status < 500) {
            // Client error — log detail, remove from queue (will not self-heal)
            let detail = '';
            try { detail = await response.text(); } catch (_) { /* ignore */ }
            console.error('[Analytics] Batch rejected (' + response.status + '):', detail);
            await this._dbDeleteBatch(ids);
        }
        // 5xx: leave in queue; flag that next interval tick must gate on a health check
        else {
            this._pendingHealthCheck = true;
        }
    },

    async _purgeStale() {
        const cutoff = Date.now() - this._TTL_MS;
        await this._dbDeleteByEnqueuedAtRange(cutoff);
    },

    async _enforceCap() {
        const count = await this._dbCount();
        if (count >= this._MAX_QUEUE_SIZE) {
            const oldest = await this._dbGetOldest(1);
            if (oldest.length > 0) {
                await this._dbDeleteBatch([oldest[0].id]);
            }
        }
    },

    async _healthCheck() {
        try {
            const response = await fetch(`${this._analyticsBaseUri}/v1/health`);
            return response.ok;
        } catch {
            return false;
        }
    },

    async _acquireToken() {
        const now = Date.now();
        if (this._surfaceToken && now < this._surfaceTokenExpiresAt) {
            return this._surfaceToken;
        }

        if (!this._sessionToken) return null;

        try {
            const response = await fetch(`${this._analyticsBaseUri}/v1/token`, {
                method:  'POST',
                headers: {
                    'Authorization': `Bearer ${this._sessionToken}`,
                    'Content-Type':  'application/json'
                },
                body: JSON.stringify({ surface: this._surface, dealership_id: this._dealershipId })
            });

            if (!response.ok) {
                console.warn('[Analytics] Token acquisition failed:', response.status);
                return null;
            }

            const data = await response.json();
            this._surfaceToken         = data.token;
            this._surfaceTokenExpiresAt = now + this._TOKEN_TTL_MS;

            // Proactive refresh 30s before expiry
            if (this._tokenRefreshTimeoutId) clearTimeout(this._tokenRefreshTimeoutId);
            const refreshIn = this._TOKEN_TTL_MS - this._TOKEN_REFRESH_EARLY_MS;
            this._tokenRefreshTimeoutId = setTimeout(() => {
                this._acquireToken().catch(err => console.warn('[Analytics] Token refresh failed:', err));
            }, refreshIn);

            return this._surfaceToken;
        } catch (err) {
            console.warn('[Analytics] Token acquisition error:', err);
            return null;
        }
    },

    /**
     * Determine surface from platform context.
     * ERA Desktop embeds FusionWeb in a frame — treat as 'desktop'.
     * All other platforms default to 'web'.
     */
    _detectSurface() {
        if (this._platform === 'ERA') return 'desktop';
        return 'web';
    },

    // ---------------------------------------------------------------------------
    // IndexedDB helpers
    // ---------------------------------------------------------------------------

    _openDb() {
        return new Promise((resolve, reject) => {
            const request = indexedDB.open(this._DB_NAME, 2);

            request.onupgradeneeded = (event) => {
                const db = event.target.result;
                if (!db.objectStoreNames.contains(this._STORE_NAME)) {
                    const store = db.createObjectStore(this._STORE_NAME, {
                        keyPath:       'id',
                        autoIncrement: true
                    });
                    // Index on enqueuedAt enables oldest-first cursor reads and range-based TTL purge
                    store.createIndex('enqueuedAt', 'enqueuedAt', { unique: false });
                }
                if (!db.objectStoreNames.contains(this._SESSION_STATE_STORE)) {
                    db.createObjectStore(this._SESSION_STATE_STORE, { keyPath: 'id' });
                }
            };

            request.onsuccess = (event) => resolve(event.target.result);
            request.onerror   = (event) => reject(event.target.error);
        });
    },

    _dbAdd(record) {
        return new Promise((resolve, reject) => {
            const tx      = this._db.transaction(this._STORE_NAME, 'readwrite');
            const request = tx.objectStore(this._STORE_NAME).add(record);
            request.onsuccess = () => resolve(request.result);
            request.onerror   = () => reject(request.error);
        });
    },

    _dbGetOldest(limit) {
        return new Promise((resolve, reject) => {
            const tx      = this._db.transaction(this._STORE_NAME, 'readonly');
            const index   = tx.objectStore(this._STORE_NAME).index('enqueuedAt');
            const results = [];
            const request = index.openCursor();

            request.onsuccess = (event) => {
                const cursor = event.target.result;
                if (cursor && results.length < limit) {
                    results.push(cursor.value);
                    cursor.continue();
                } else {
                    resolve(results);
                }
            };
            request.onerror = () => reject(request.error);
        });
    },

    _dbDeleteBatch(ids) {
        return new Promise((resolve, reject) => {
            if (ids.length === 0) { resolve(); return; }

            const tx    = this._db.transaction(this._STORE_NAME, 'readwrite');
            const store = tx.objectStore(this._STORE_NAME);
            let pending = ids.length;

            ids.forEach(id => {
                const req = store.delete(id);
                req.onsuccess = () => { if (--pending === 0) resolve(); };
                req.onerror   = () => reject(req.error);
            });
        });
    },

    _dbDeleteByEnqueuedAtRange(upperBoundExclusive) {
        return new Promise((resolve, reject) => {
            const tx      = this._db.transaction(this._STORE_NAME, 'readwrite');
            const index   = tx.objectStore(this._STORE_NAME).index('enqueuedAt');
            const range   = IDBKeyRange.upperBound(upperBoundExclusive, true /* exclusive */);
            const request = index.openCursor(range);

            request.onsuccess = (event) => {
                const cursor = event.target.result;
                if (cursor) {
                    cursor.delete();
                    cursor.continue();
                } else {
                    resolve();
                }
            };
            request.onerror = () => reject(request.error);
        });
    },

    _dbCount() {
        return new Promise((resolve, reject) => {
            const tx      = this._db.transaction(this._STORE_NAME, 'readonly');
            const request = tx.objectStore(this._STORE_NAME).count();
            request.onsuccess = () => resolve(request.result);
            request.onerror   = () => reject(request.error);
        });
    },

    _sessionStateGetAll() {
        return new Promise((resolve, reject) => {
            const tx      = this._db.transaction(this._SESSION_STATE_STORE, 'readonly');
            const request = tx.objectStore(this._SESSION_STATE_STORE).getAll();
            request.onsuccess = () => resolve(request.result ?? []);
            request.onerror   = () => reject(request.error);
        });
    },

    _sessionStatePut(record) {
        return new Promise((resolve, reject) => {
            const tx      = this._db.transaction(this._SESSION_STATE_STORE, 'readwrite');
            const request = tx.objectStore(this._SESSION_STATE_STORE).put(record);
            request.onsuccess = () => resolve();
            request.onerror   = () => reject(request.error);
        });
    },

    _sessionStateGet(sessionId) {
        return new Promise((resolve, reject) => {
            const tx      = this._db.transaction(this._SESSION_STATE_STORE, 'readonly');
            const request = tx.objectStore(this._SESSION_STATE_STORE).get(sessionId);
            request.onsuccess = () => resolve(request.result ?? null);
            request.onerror   = () => reject(request.error);
        });
    },

    // Atomic get-then-update-last_active_at-if-exists within a single readwrite transaction.
    // Preserves action_count so cross-frame increments don't get clobbered by a touch.
    // Returns the updated record, or null if the record was missing (do not recreate).
    _sessionStateTouchIfExists(sessionId) {
        return new Promise((resolve, reject) => {
            const tx      = this._db.transaction(this._SESSION_STATE_STORE, 'readwrite');
            const store   = tx.objectStore(this._SESSION_STATE_STORE);
            const getReq  = store.get(sessionId);
            getReq.onsuccess = () => {
                if (!getReq.result) {
                    resolve(null);
                    return;
                }
                const record = getReq.result;
                record.last_active_at = Date.now();
                const putReq = store.put(record);
                putReq.onsuccess = () => resolve(record);
                putReq.onerror   = () => reject(putReq.error);
            };
            getReq.onerror = () => reject(getReq.error);
        });
    },

    // Atomic get-then-delete in a single readwrite transaction. IDB serialises transactions
    // on the store, so for concurrent claims of the same sessionId exactly one caller's get
    // returns the record; the others' gets return null.
    _sessionStateClaimAndDelete(sessionId) {
        return new Promise((resolve, reject) => {
            const tx      = this._db.transaction(this._SESSION_STATE_STORE, 'readwrite');
            const store   = tx.objectStore(this._SESSION_STATE_STORE);
            const getReq  = store.get(sessionId);
            getReq.onsuccess = () => {
                if (!getReq.result) {
                    resolve(null);
                    return;
                }
                const record = getReq.result;
                const delReq = store.delete(sessionId);
                delReq.onsuccess = () => resolve(record);
                delReq.onerror   = () => reject(delReq.error);
            };
            getReq.onerror = () => reject(getReq.error);
        });
    },

    // Atomic read-increment-write in a single readwrite transaction. IDB serialises writes
    // per store, so concurrent increments from multiple frames compose correctly.
    // Returns the updated record, or null if missing (do not recreate).
    _sessionStateIncrementActionCount(sessionId) {
        return new Promise((resolve, reject) => {
            const tx      = this._db.transaction(this._SESSION_STATE_STORE, 'readwrite');
            const store   = tx.objectStore(this._SESSION_STATE_STORE);
            const getReq  = store.get(sessionId);
            getReq.onsuccess = () => {
                if (!getReq.result) {
                    resolve(null);
                    return;
                }
                const record = getReq.result;
                record.action_count   = (record.action_count || 0) + 1;
                record.last_active_at = Date.now();
                const putReq = store.put(record);
                putReq.onsuccess = () => resolve(record);
                putReq.onerror   = () => reject(putReq.error);
            };
            getReq.onerror = () => reject(getReq.error);
        });
    },

    _sessionStateDelete(key) {
        return new Promise((resolve, reject) => {
            const tx      = this._db.transaction(this._SESSION_STATE_STORE, 'readwrite');
            const request = tx.objectStore(this._SESSION_STATE_STORE).delete(key);
            request.onsuccess = () => resolve();
            request.onerror   = () => reject(request.error);
        });
    }
};

window.Analytics = Analytics;
